import { ShieldCheck, Award, FileCheck2, AlertCircle } from 'lucide-react';

/**
 * Compact authenticity badge for prize listings (game rooms / auctions).
 * Displays grading service + grade + cert verification status.
 */
export function AuthenticityBadge({ condition, grade, certNumber, verified }) {
  if (!condition && !grade && !certNumber) return null;

  const isGraded = condition && condition !== 'raw';
  const serviceLabel = condition ? condition.toUpperCase() : 'RAW';

  return (
    <div data-testid="authenticity-badge" className="flex flex-wrap items-center gap-1.5">
      {isGraded ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] font-bold text-amber-300 uppercase tracking-wider">
          <Award className="w-2.5 h-2.5" />
          {serviceLabel}
          {grade && <span className="text-amber-200">{grade}</span>}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-700/40 border border-slate-600/40 text-[10px] font-medium text-slate-300 uppercase">
          Raw
        </span>
      )}
      {certNumber && (
        <span
          data-testid="cert-badge"
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
            verified
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
              : 'bg-slate-700/40 border-slate-600/40 text-slate-300'
          }`}
        >
          {verified ? <ShieldCheck className="w-2.5 h-2.5" /> : <FileCheck2 className="w-2.5 h-2.5" />}
          Cert #{certNumber}
          {verified && <span className="text-emerald-400">verified</span>}
        </span>
      )}
    </div>
  );
}

/**
 * Detailed authenticity panel for full pages. Shows trust pillars + cert link.
 */
export function AuthenticityPanel({ condition, grade, certNumber, verified, lookupUrl }) {
  if (!condition && !grade && !certNumber) return null;

  return (
    <div data-testid="authenticity-panel" className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-400" />
        <p className="text-xs font-bold text-white uppercase tracking-wide">Authenticity</p>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        {condition && (
          <div>
            <p className="text-slate-500 text-[9px] uppercase">Type</p>
            <p className="text-white font-semibold">{condition.toUpperCase()}</p>
          </div>
        )}
        {grade && (
          <div>
            <p className="text-slate-500 text-[9px] uppercase">Grade</p>
            <p className="text-amber-300 font-semibold">{grade}</p>
          </div>
        )}
        {certNumber && (
          <div>
            <p className="text-slate-500 text-[9px] uppercase">Cert</p>
            <p className="text-white font-mono">#{certNumber}</p>
          </div>
        )}
      </div>
      {certNumber && (
        <div className={`flex items-center gap-1.5 text-[10px] pt-1 border-t border-slate-700/40 ${
          verified ? 'text-emerald-400' : 'text-amber-400'
        }`}>
          {verified ? <ShieldCheck className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
          {verified ? 'Verified by grading service' : 'Pending manual verification'}
          {lookupUrl && (
            <a
              data-testid="cert-lookup-link"
              href={lookupUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-indigo-300 hover:text-indigo-200 underline"
            >
              Verify on official site
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default AuthenticityBadge;
